import * as React from "react";
import {
  Card,
  Subtitle1,
  Subtitle2,
  Text,
  Input,
  Button,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { Add20Regular, Edit20Regular, Delete20Regular, Save20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import {
  Verantwortlichkeit,
  VerantwortlichkeitInput,
  listVerantwortlichkeiten,
  createVerantwortlichkeit,
  updateVerantwortlichkeit,
  deleteVerantwortlichkeit,
} from "../../services/verantwortlichkeitService";
import { invalidateVerantwortlichkeitenCache } from "../../components/verantwortlichkeit";
import { useBestaetigen } from "../../components/redesign/bestaetigen";

type Draft = VerantwortlichkeitInput;

const emptyDraft = (): Draft => ({ bereich: "", art: "intern", bezeichnung: "" });

const ArtToggle: React.FC<{ value: "intern" | "extern"; onChange: (v: "intern" | "extern") => void }> = ({
  value,
  onChange,
}) => (
  <div style={{ display: "inline-flex", gap: 6 }}>
    {(["intern", "extern"] as const).map((a) => (
      <Button
        key={a}
        size="small"
        appearance={value === a ? "primary" : "secondary"}
        onClick={() => onChange(a)}
      >
        {a === "intern" ? "Intern" : "Extern"}
      </Button>
    ))}
  </div>
);

export const ListsSection: React.FC = () => {
  const { bestaetigen } = useBestaetigen();
  const [items, setItems] = React.useState<Verantwortlichkeit[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  // Add-Formular (mit optionalem Prefill für „+ Rolle" je Bereich).
  const [addOpen, setAddOpen] = React.useState(false);
  const [addDraft, setAddDraft] = React.useState<Draft>(emptyDraft());

  // Inline-Edit einer Rolle.
  const [editId, setEditId] = React.useState<number | null>(null);
  const [editDraft, setEditDraft] = React.useState<Draft>(emptyDraft());

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listVerantwortlichkeiten());
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // Nach jeder Änderung: Liste neu laden + den modul-gecachten Katalog der
  // Eingabe-Komponente invalidieren (frische Daten ohne Reload).
  const refresh = React.useCallback(async () => {
    invalidateVerantwortlichkeitenCache();
    await load();
  }, [load]);

  // Gruppierung: Bereich → Rollen (Reihenfolge wie geliefert = sortOrder).
  const groups = React.useMemo(() => {
    const byBereich = new Map<string, Verantwortlichkeit[]>();
    for (const it of items) {
      if (!byBereich.has(it.bereich)) byBereich.set(it.bereich, []);
      byBereich.get(it.bereich)!.push(it);
    }
    return byBereich;
  }, [items]);

  const bereiche = React.useMemo(() => Array.from(new Set(items.map((i) => i.bereich))), [items]);

  const openAdd = (prefill?: Partial<Draft>) => {
    setAddDraft({ ...emptyDraft(), ...prefill });
    setAddOpen(true);
  };

  const submitAdd = async () => {
    const d = addDraft;
    if (!d.bereich.trim() || !d.bezeichnung.trim()) {
      setError("Bitte Bereich und Bezeichnung ausfüllen.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createVerantwortlichkeit({
        bereich: d.bereich.trim(),
        art: d.art,
        bezeichnung: d.bezeichnung.trim(),
      });
      setAddOpen(false);
      setAddDraft(emptyDraft());
      await refresh();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (it: Verantwortlichkeit) => {
    setEditId(it.id);
    setEditDraft({ bereich: it.bereich, art: it.art, bezeichnung: it.bezeichnung });
  };

  const submitEdit = async () => {
    if (editId == null) return;
    const d = editDraft;
    if (!d.bereich.trim() || !d.bezeichnung.trim()) {
      setError("Bitte Bereich und Bezeichnung ausfüllen.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateVerantwortlichkeit(editId, {
        bereich: d.bereich.trim(),
        art: d.art,
        bezeichnung: d.bezeichnung.trim(),
      });
      setEditId(null);
      await refresh();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it: Verantwortlichkeit) => {
    const ok = await bestaetigen({
      titel: "Eintrag löschen?",
      text: `„${it.bezeichnung}" (${it.art}) wird aus der Liste entfernt.`,
      ja: "Löschen",
      ton: "danger",
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await deleteVerantwortlichkeit(it.id);
      await refresh();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const draftFields = (d: Draft, set: (patch: Partial<Draft>) => void) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
      <Input
        placeholder="Bereich"
        value={d.bereich}
        list="verant-bereiche"
        onChange={(_, v) => set({ bereich: v.value })}
        style={{ minWidth: 180 }}
      />
      <ArtToggle value={d.art} onChange={(art) => set({ art })} />
      <Input
        placeholder="Verantwortlichkeit / Rolle"
        value={d.bezeichnung}
        onChange={(_, v) => set({ bezeichnung: v.value })}
        style={{ minWidth: 240 }}
      />
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <datalist id="verant-bereiche">
        {bereiche.map((b) => (
          <option key={b} value={b} />
        ))}
      </datalist>

      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Subtitle1>Verantwortlichkeiten</Subtitle1>
            <Text size={200} style={{ color: "#6b5a66" }}>
              Bereich → konkrete Rolle (intern/extern). Wird systemweit für die Erfassung von Verantwortlichkeiten
              genutzt. Der Bereich „Sonstige" blendet bei der Erfassung ein Freitextfeld ein — dafür sind hier keine
              Einträge nötig.
            </Text>
          </div>
          <Button appearance="primary" icon={<Add20Regular />} onClick={() => openAdd()} disabled={busy}>
            Neue Verantwortlichkeit
          </Button>
        </div>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {addOpen && (
          <Card appearance="filled-alternative" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <Subtitle2>Neue Verantwortlichkeit</Subtitle2>
            {draftFields(addDraft, (patch) => setAddDraft((p) => ({ ...p, ...patch })))}
            <div style={{ display: "flex", gap: 8 }}>
              <Button appearance="primary" icon={<Save20Regular />} onClick={submitAdd} disabled={busy}>
                Hinzufügen
              </Button>
              <Button icon={<Dismiss20Regular />} onClick={() => setAddOpen(false)} disabled={busy}>
                Abbrechen
              </Button>
            </div>
          </Card>
        )}

        {loading ? (
          <Spinner label="Lade Liste…" />
        ) : items.length === 0 ? (
          <Text style={{ color: "#6b5a66" }}>Noch keine Einträge.</Text>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {Array.from(groups.entries()).map(([bereich, rollen]) => (
              <div
                key={bereich}
                style={{
                  border: "1px solid #ECE7F0",
                  borderRadius: 10,
                  padding: "10px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <Subtitle2>{bereich}</Subtitle2>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<Add20Regular />}
                    onClick={() => openAdd({ bereich })}
                  >
                    Rolle
                  </Button>
                </div>
                {rollen.map((it) =>
                  editId === it.id ? (
                    <div key={it.id} style={{ display: "flex", flexDirection: "column", gap: 8, padding: "6px 0" }}>
                      {draftFields(editDraft, (patch) => setEditDraft((p) => ({ ...p, ...patch })))}
                      <div style={{ display: "flex", gap: 8 }}>
                        <Button size="small" appearance="primary" icon={<Save20Regular />} onClick={submitEdit} disabled={busy}>
                          Speichern
                        </Button>
                        <Button size="small" icon={<Dismiss20Regular />} onClick={() => setEditId(null)} disabled={busy}>
                          Abbrechen
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={it.id}
                      style={{ display: "flex", alignItems: "center", gap: 10, paddingLeft: 4 }}
                    >
                      <span style={{ color: "#a294a0" }}>•</span>
                      <Text style={{ flexGrow: 1 }}>{it.bezeichnung}</Text>
                      <Badge appearance="tint" color={it.art === "extern" ? "warning" : "brand"}>
                        {it.art === "extern" ? "extern" : "intern"}
                      </Badge>
                      <Button size="small" appearance="subtle" icon={<Edit20Regular />} title="Bearbeiten" onClick={() => startEdit(it)} />
                      <Button size="small" appearance="subtle" icon={<Delete20Regular />} title="Löschen" onClick={() => remove(it)} />
                    </div>
                  ),
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
